import { Fragment, useMemo, type ReactNode } from 'react'
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import Image from 'components/Common/Image'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import Divider from 'LuxKit/Divider'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ItineraryMap from './ItineraryMap'
import type { MapHandle } from 'lib/maps/types'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  CRUISE_MINUTES_PER_DAY,
  getPortMinutesAshore,
  getSeaSegmentMinutes,
} from 'lib/cruises/cruiseUtils'
import { isCruiseItineraryDayAtSea } from 'lib/cruises/cruiseOfferUtils'

const PLACEHOLDER_IMAGE_ID = 'szx0yiy4fy4pm81q9te'

const OverviewContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

const ImageCard = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borderRadius.S};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  box-shadow: ${props => props.theme.shadow.flat.medium};
`

const ImageWrapper = styled.div`
  position: relative;
  height: ${rem(150)};

  ${mediaQueryUp.tablet} {
    height: ${rem(320)};
  }
`

const BackgroundImage = styled(Image)`
  position: absolute;
  inset: 0;
  border-radius: ${props => props.theme.borderRadius.S};
`

const GradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgb(1 8 26 / 50%) 0%, rgb(1 8 37 / 50%) 30%, transparent 100%);
  border-radius: ${props => props.theme.borderRadius.S};
`

const ImageContent = styled.div`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  padding: ${rem(24)};
`

const MapWrapper = styled.div`
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadow.flat.medium};
`

const InfoCard = styled.div`
  padding: ${rem(12)} ${rem(16)};
  background: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.S};
  box-shadow: ${props => props.theme.shadow.flat.medium};
`

interface Props {
  item: App.CruiseItineraryItem
  itinerary: Array<App.CruiseItineraryItem>
  itineraryItem: App.Cruises.OfferItineraryV2Item
  offerFallbackImageId?: string
  portNameSelected?: string
  onSelectPort?: (portName: string | null) => void
  onMapLoaded?: (map: MapHandle) => void
}

function CruiseItineraryItemOverview({
  item,
  itinerary,
  itineraryItem,
  offerFallbackImageId,
  portNameSelected,
  onSelectPort,
  onMapLoaded,
}: Props) {
  const intl = useIntl()
  const {
    startDay,
    imageId,
    description: offerItineraryDescription,
    startTime,
    endTime,
    attractions,
  } = itineraryItem

  const portName = item.port?.name ?? item.title
  const heroImageId =
    attractions[0]?.imageId ?? imageId ?? offerFallbackImageId ?? PLACEHOLDER_IMAGE_ID

  const isDayAtSea = isCruiseItineraryDayAtSea(item)

  const itemIndex = useMemo(
    () =>
      itinerary.findIndex(
        i =>
          i.startDay === item.startDay &&
          i.title === item.title &&
          i.startTime === item.startTime &&
          i.endTime === item.endTime,
      ),
    [itinerary, item.startDay, item.title, item.startTime, item.endTime],
  )

  const seaMinutes = useMemo(() => {
    if (itemIndex < 0 || !isDayAtSea) {
      return undefined
    }
    return getSeaSegmentMinutes(itinerary, itemIndex)
  }, [itinerary, itemIndex, isDayAtSea])

  const atSeaDurationLabel = useMemo(() => {
    if (seaMinutes === undefined || seaMinutes <= 0) {
      return undefined
    }
    return formatAtSeaDurationLabel(intl, seaMinutes)
  }, [intl, seaMinutes])

  const ashoreMinutes = useMemo(() => {
    if (!item.port) {
      return undefined
    }
    return getPortMinutesAshore(startTime, endTime)
  }, [item.port, startTime, endTime])

  const ashoreDurationLabel = useMemo(() => {
    if (ashoreMinutes === undefined || ashoreMinutes <= 0) {
      return undefined
    }
    return formatPortAshoreDurationLabel(intl, ashoreMinutes)
  }, [intl, ashoreMinutes])

  const infoBarSegments = useMemo(() => {
    type Segment = { key: string; startIcon: ReactNode; content: ReactNode }
    const segments: Array<Segment> = []

    if (startTime !== '--') {
      segments.push({
        key: 'arrival',
        startIcon: <LineMapMarkerIcon />,
        content: intl.formatMessage(
          {
            id: '5Q4PcKew9drZa1_55SNd',
            defaultMessage: 'Arrival: <bold>{time}</bold>',
            description:
              'Cruise itinerary hero image info bar: arrival label and time for the selected day; time is emphasised',
          },
          {
            time: startTime,
            bold: (chunks: React.ReactNode) => <b key="arrival-time">{chunks}</b>,
          },
        ),
      })
    }

    if (endTime !== '--') {
      segments.push({
        key: 'departure',
        startIcon: <LineMapMarkerIcon />,
        content: intl.formatMessage(
          {
            id: 'a4dqMnej0rwc8z1fE95E',
            defaultMessage: 'Departure: <bold>{time}</bold>',
            description:
              'Cruise itinerary hero image info bar: departure label and time for the selected day; time is emphasised',
          },
          {
            time: endTime,
            bold: (chunks: React.ReactNode) => <b key="departure-time">{chunks}</b>,
          },
        ),
      })
    }

    if (!isDayAtSea && ashoreDurationLabel) {
      segments.push({
        key: 'ashore-duration',
        startIcon: <LineClockIcon size="S" />,
        content: <b key="ashore-duration">{ashoreDurationLabel}</b>,
      })
    }

    if (isDayAtSea && atSeaDurationLabel) {
      segments.push({
        key: 'at-sea-duration',
        startIcon: <LineClockIcon size="S" />,
        content: <b key="at-sea-duration">{atSeaDurationLabel}</b>,
      })
    }

    return segments
  }, [
    intl,
    startTime,
    endTime,
    isDayAtSea,
    ashoreDurationLabel,
    atSeaDurationLabel,
  ])

  const dayBlurb = useMemo(() => {
    if (!item.port && !isDayAtSea) {
      return item.description?.trim() || item.title
    }
    const fromOfferItinerary = offerItineraryDescription.trim()
    if (fromOfferItinerary) {
      return fromOfferItinerary
    }
    return item.description?.trim() ?? ''
  }, [offerItineraryDescription, item.description, item.title, item.port, isDayAtSea])

  const overviewSectionHeading = (
    <FormattedMessage
      id="D_9t_PPVDtn2Uxwl2Xjp"
      defaultMessage="Make the most of your journey"
      description="Section title on cruise itinerary overview for the selected day; shown for both at-sea days and in-port days"
    />
  )

  return (
    <Group direction="vertical" gap={16}>
      <CSSBreakpoint min="tablet">
        <Heading variant="heading4">
          {overviewSectionHeading}
        </Heading>
      </CSSBreakpoint>
      <OverviewContainer>
        <CSSBreakpoint min="tablet">
          <ImageCard>
            <ImageWrapper>
              <BackgroundImage
                id={heroImageId}
                alt={portName}
                fit="center"
                objectFit="cover"
              />
              <GradientOverlay />
              <ImageContent>
                <Group direction="vertical" gap={4}>
                  <Heading variant="heading3" colour="neutral-eight">
                    <FormattedMessage
                      id="7UZX7VsN58ZjEXsFisyN"
                      defaultMessage="Day {day}"
                      description="Day number label in trip itinerary item location summary"
                      values={{ day: startDay }}
                    />
                  </Heading>
                  <BodyText variant="large" colour="neutral-eight">
                    {portName}
                  </BodyText>
                </Group>
              </ImageContent>
            </ImageWrapper>
          </ImageCard>
        </CSSBreakpoint>

        <MapWrapper>
          <ItineraryMap
            itinerary={itinerary}
            portNameSelected={portNameSelected}
            onSelectPort={onSelectPort}
            onMapLoaded={onMapLoaded}
          />
        </MapWrapper>
      </OverviewContainer>

      {(dayBlurb || infoBarSegments.length > 0) && (
        <InfoCard>
          <Group direction="vertical" gap={12}>
            {dayBlurb && (
              <>
                <BodyText variant="medium" colour="neutral-one">
                  {dayBlurb}
                </BodyText>
                {infoBarSegments.length > 0 && (
                  <Divider kind="secondary" direction="horizontal" />
                )}
              </>
            )}

            {infoBarSegments.length > 0 && (
              <Group direction="horizontal" gap={16} verticalAlign="center">
                {infoBarSegments.map((segment, segmentIndex) => (
                  <Fragment key={segment.key}>
                    {segmentIndex > 0 && <Divider kind="primary" direction="vertical" />}
                    <Group direction="vertical" gap={4}>
                      <CaptionTextBlock variant="large" startIcon={segment.startIcon}>
                        {segment.content}
                      </CaptionTextBlock>
                    </Group>
                  </Fragment>
                ))}
              </Group>
            )}
          </Group>
        </InfoCard>
      )}
    </Group>
  )
}

function formatPortAshoreDurationLabel(intl: IntlShape, totalMinutes: number): string {
  if (totalMinutes > CRUISE_MINUTES_PER_DAY) {
    return intl.formatMessage({
      id: 'XiBZL51cNIKl9EB8VYeG',
      defaultMessage: 'Whole day ashore',
      description:
        'Label when time in port between arrival and departure is longer than 24 hours',
    })
  }

  const totalHours = Math.round(totalMinutes / 60)
  return intl.formatMessage(
    {
      id: 'dWuZEVjXak-sSMXb6Vzp',
      defaultMessage: '{hours, plural, one {# hour ashore} other {# hours ashore}}',
      description: 'Rounded hours guests can spend ashore between arrival and departure at a port',
    },
    { hours: totalHours },
  )
}

function formatAtSeaDurationLabel(intl: IntlShape, totalMinutes: number): string {
  if (totalMinutes > CRUISE_MINUTES_PER_DAY) {
    return intl.formatMessage({
      id: 'Pp-RNDIrQgnT8KOosX7_',
      defaultMessage: 'Whole day at sea',
      description:
        'Label when the sailing gap between ports is longer than 24 hours (multi-day at sea segment)',
    })
  }

  const totalHours = Math.round(totalMinutes / 60)
  return intl.formatMessage(
    {
      id: 'Dth26Gp5O_344Ix2On23',
      defaultMessage: '{hours, plural, one {# hour at sea} other {# hours at sea}}',
      description:
        'Total rounded hours sailing between the previous port departure and next port arrival on a cruise',
    },
    { hours: totalHours },
  )
}

export default CruiseItineraryItemOverview
